const template = document.createElement('template');
template.innerHTML = `
<style>
  :host {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    width: 60px;
    height: 70px;
    user-select: none;
    --knob-size: 40px;
    --indicator-color: #00ffcc;
    --track-color: #333;
  }
  
  .knob-container {
    position: relative;
    width: var(--knob-size);
    height: var(--knob-size);
    border-radius: 50%;
    background: #222;
    border: 2px solid #444;
    cursor: ns-resize;
    box-shadow: 0 2px 5px rgba(0,0,0,0.5);
    transition: border-color 0.2s;
  }

  .knob-container:hover {
    border-color: #666;
  }

  .knob-container:active {
    border-color: var(--indicator-color);
  }

  /* SVG Indicator */
  svg {
    width: 100%;
    height: 100%;
    transform: rotate(-90deg); /* Start from bottom? No, standard is -135 to +135 usually */
    pointer-events: none;
  }

  circle.track {
    fill: none;
    stroke: var(--track-color);
    stroke-width: 4;
  }

  circle.value {
    fill: none;
    stroke: var(--indicator-color);
    stroke-width: 4;
    stroke-linecap: round;
    stroke-dasharray: 100; /* Calculated in JS */
    stroke-dashoffset: 50;
    transition: stroke-dashoffset 0.1s;
  }

  .indicator-dot {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 4px;
    height: 50%;
    transform-origin: bottom center;
    transform: translate(-50%, -100%) rotate(0deg);
    pointer-events: none;
  }

  .indicator-dot::after {
    content: '';
    position: absolute;
    top: 5px;
    left: 50%;
    transform: translateX(-50%);
    width: 4px;
    height: 4px;
    background: #fff;
    border-radius: 50%;
    box-shadow: 0 0 5px var(--indicator-color);
  }

  .label {
    margin-top: 4px;
    font-size: 8px;
    color: #aaa;
    font-family: inherit;
    text-transform: uppercase;
    text-align: center;
    pointer-events: none;
  }
  
  .val-display {
      position: absolute;
      top: -20px;
      background: rgba(0,0,0,0.8);
      color: var(--indicator-color);
      padding: 2px 4px;
      font-size: 10px;
      border-radius: 3px;
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.2s;
  }

  :host(:active) .val-display,
  .knob-container.active .val-display {
      opacity: 1;
  }

</style>
<div class="knob-container" id="knob">
  <svg viewBox="0 0 40 40">
      <circle class="track" cx="20" cy="20" r="16" stroke-dasharray="75 100" stroke-dashoffset="-12.5"></circle> <!-- 270 degree arc -->
      <circle class="value" cx="20" cy="20" r="16"></circle>
  </svg>
  <div class="indicator-dot"></div>
  <div class="val-display">0.0</div>
</div>
<div class="label"></div>
`;

export class RotaryKnob extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot.appendChild(template.content.cloneNode(true));
    
    // Bindings
    this.handleDown = this.handleDown.bind(this);
    this.handleMove = this.handleMove.bind(this);
    this.handleUp = this.handleUp.bind(this);

    // State
    this._value = 0;
    this.startY = 0;
    this.startVal = 0;
  }

  static get observedAttributes() {
    return ['min', 'max', 'value', 'step', 'label', 'param', 'fx', 'data-labels'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'label') {
      this.shadowRoot.querySelector('.label').textContent = newValue;
    } else {
      this.render();
    }
  }

  connectedCallback() {
    this.knob = this.shadowRoot.getElementById('knob');
    this.knob.addEventListener('mousedown', this.handleDown);
    this.knob.addEventListener('touchstart', this.handleDown, { passive: false });
    
    // Set initial value
    if (!this.hasAttribute('value')) this.setAttribute('value', 0);
    this._value = parseFloat(this.getAttribute('value'));
    this.render();
  }

  get min() { return parseFloat(this.getAttribute('min') || 0); }
  get max() { return parseFloat(this.getAttribute('max') || 1); }
  get step() { return parseFloat(this.getAttribute('step') || 0.01); }
  get value() { return this._value; }

  set value(val) {
      this._value = Math.max(this.min, Math.min(this.max, val));
      this.setAttribute('value', this._value);
      this.render();
      this.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
  }

  handleDown(e) {
    e.preventDefault(); // Prevent scrolling on touch
    this.knob.classList.add('active');
    this.startY = e.clientY || e.touches[0].clientY;
    this.startVal = this._value;
    
    window.addEventListener('mousemove', this.handleMove);
    window.addEventListener('mouseup', this.handleUp);
    window.addEventListener('touchmove', this.handleMove, { passive: false });
    window.addEventListener('touchend', this.handleUp);
  }

  handleMove(e) {
    const clientY = e.clientY || (e.touches ? e.touches[0].clientY : 0);
    const deltaY = this.startY - clientY; // Up is positive
    
    // Sensitivity
    const range = this.max - this.min;
    const pxRange = 150; // Pixels to cover full range
    const deltaVal = (deltaY / pxRange) * range;

    let newVal = this.startVal + deltaVal;
    
    // Quantize to step
    newVal = Math.round(newVal / this.step) * this.step;
    
    // Clamp
    newVal = Math.max(this.min, Math.min(this.max, newVal));

    if (newVal !== this._value) {
        this.value = newVal;
    }
  }

  handleUp() {
    this.knob.classList.remove('active');
    window.removeEventListener('mousemove', this.handleMove);
    window.removeEventListener('mouseup', this.handleUp);
    window.removeEventListener('touchmove', this.handleMove);
    window.removeEventListener('touchend', this.handleUp);
  }

  render() {
    const min = this.min;
    const max = this.max;
    const val = this._value;
    
    // Calculate percentage (0 to 1)
    const pct = (val - min) / (max - min);

    // Map to rotation (-135 to 135 degrees = 270 range)
    const deg = -135 + (pct * 270);
    
    // Update Dot
    const dot = this.shadowRoot.querySelector('.indicator-dot');
    dot.style.transform = `translate(-50%, -100%) rotate(${deg}deg)`;

    // Update SVG Stroke (Track is 100 units long approx, let's just use stroke-dasharray)
    // Circle r=16, C = 2*pi*16 ≈ 100.5
    // We want 75% of circle visible (270deg).
    // stroke-dasharray="75 100" sets the visible arc.
    // stroke-dashoffset controls the start. 
    // Actually simpler: Just rotate the dot. The SVG ring is just decoration or background.
    // Let's make the colored ring follow.
    
    const circle = this.shadowRoot.querySelector('.value');
    const circumference = 2 * Math.PI * 16;
    const maxDash = circumference * 0.75; // 270 degrees
    const dash = pct * maxDash;
    
    circle.style.strokeDasharray = `${dash} ${circumference}`;
    circle.style.strokeDashoffset = 0; // Starts from 3 o'clock by default
    // We need to rotate the whole SVG to match start point -135deg
    this.shadowRoot.querySelector('svg').style.transform = 'rotate(135deg)';
    
    // Update Display
    const display = this.shadowRoot.querySelector('.val-display');
    
    if (this.hasAttribute('data-labels')) {
        const labels = this.getAttribute('data-labels').split(',');
        // Map value to index
        // Assuming integer steps corresponding to indices
        const index = Math.round(val);
        if (labels[index] !== undefined) {
            display.textContent = labels[index].trim();
        } else {
            display.textContent = val;
        }
    } else {
        display.textContent = val.toFixed(this.step < 0.1 ? 2 : 1);
    }
  }
}

customElements.define('rotary-knob', RotaryKnob);
